"use client";

import Image from "next/image";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useCheckout } from "@/hooks/use-checkout";
import { toast } from "sonner";
import { PREVIEW_CTA_PACK_ID, getPackPriceUsd } from "@/lib/payments/client";

interface StickyGenerateCTAProps {
  onGenerate: () => void;
  onGenerateFreePreview?: () => void;
  isGenerating: boolean;
  canGenerate: boolean;
  creditBalance: number;
  isEligibleForFreePreview: boolean;
  hasVehicle: boolean;
  hasWheel: boolean;
  hasVehiclesInGarage?: boolean;
  vehicleImageUrl?: string | null;
  className?: string;
}

export function StickyGenerateCTA({
  onGenerate,
  onGenerateFreePreview,
  isGenerating,
  canGenerate,
  creditBalance,
  isEligibleForFreePreview,
  hasVehicle,
  hasWheel,
  hasVehiclesInGarage = false,
  vehicleImageUrl,
  className,
}: StickyGenerateCTAProps) {
  const checkout = useCheckout();
  const ctaPackPrice = getPackPriceUsd(PREVIEW_CTA_PACK_ID);

  // Calculate generations available (1 credit per generation)
  const generationsAvailable = creditBalance;
  const hasCredits = generationsAvailable > 0 || isEligibleForFreePreview;
  
  // User needs to purchase if they have no credits and are not eligible for free preview
  const needsPurchase = !hasCredits && hasVehicle && hasWheel;

  // Determine button state and text
  const getButtonText = () => {
    if (isGenerating) {
      return "Generating...";
    }
    if (checkout.isPending) {
      return "Processing...";
    }
    if (!hasVehicle) {
      return hasVehiclesInGarage ? "Select or upload a car" : "Upload a car";
    }
    if (!hasWheel) {
      return "Select a wheel";
    }
    if (isEligibleForFreePreview && onGenerateFreePreview) {
      return "Create Free Preview";
    }
    return "Generate HD Preview · 1 credit";
  };

  const handleClick = () => {
    if (needsPurchase) {
      // User has no credits and has used free preview - go to checkout
      checkout.mutate(
        { packId: PREVIEW_CTA_PACK_ID, source: "sticky_generate_cta" },
        {
          onError: (error) => {
            console.error("Checkout error:", error);
            toast.error(error instanceof Error ? error.message : "Failed to start checkout");
          },
        }
      );
    } else if (isEligibleForFreePreview && onGenerateFreePreview) {
      onGenerateFreePreview();
    } else {
      onGenerate();
    }
  };

  return (
    <div
      className={cn(
        "fixed bottom-0 left-0 right-0 z-50",
        "bg-background/95 backdrop-blur-md border-t border-white/10",
        "pb-[env(safe-area-inset-bottom)]",
        className
      )}
    >
      <div className="max-w-lg mx-auto px-4 py-3">
        {hasVehicle && (
          <div className="mb-2 flex items-center gap-2 text-xs text-zinc-300">
            {vehicleImageUrl ? (
              <div className="h-8 w-8 shrink-0 overflow-hidden rounded-md border border-white/10 bg-zinc-900">
                {/* A small visual anchor makes the final action feel tied to the user's car. */}
                <Image src={vehicleImageUrl} alt="Selected car" width={32} height={32} className="h-full w-full object-cover" />
              </div>
            ) : null}
            <span className="truncate">
              {hasWheel ? "Car and wheel selected — ready to visualize" : "Car selected — choose a wheel to continue"}
            </span>
          </div>
        )}
        {/* Helper text - only show when ready to generate but missing credits */}
        {hasVehicle && hasWheel && !hasCredits && !isGenerating && (
          <p className="text-center text-xs font-medium text-emerald-400 mb-2">
            High-resolution previews • No watermark • Custom wheel uploads
          </p>
        )}

        {/* Generate button */}
        <Button
          onClick={handleClick}
          disabled={needsPurchase ? checkout.isPending : (!canGenerate || isGenerating)}
          translate="no"
          data-no-translate="true"
          size="lg"
          className={cn(
            "w-full h-14 text-base font-semibold shadow-lg",
            isEligibleForFreePreview
              ? "bg-linear-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 shadow-emerald-500/20"
              : "bg-primary hover:bg-primary/90 shadow-primary/20",
            needsPurchase && "bg-emerald-600 hover:bg-emerald-500 shadow-emerald-500/20"
          )}
        >
          {isGenerating || checkout.isPending ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              {checkout.isPending ? "Processing..." : "Generating..."}
            </>
          ) : (
            <>
              {needsPurchase ? `Generate HD Images – $${ctaPackPrice.toFixed(2)}` : getButtonText()}
            </>
          )}
        </Button>
        {hasVehicle && hasWheel && hasCredits && !isGenerating && !checkout.isPending && (
          <p className="text-center text-[10px] text-zinc-500 mt-2">
            {isEligibleForFreePreview
              ? "Your free evaluation preview will be watermarked"
              : "High-resolution preview · No watermark"}
          </p>
        )}
        {!hasCredits && hasVehicle && hasWheel && !isGenerating && (
          <p className="text-center text-[10px] text-zinc-500 mt-2">
            No subscription · Credits never expire
          </p>
        )}
      </div>
    </div>
  );
}
